import type { JSX } from 'react'
import { StrictMode, Suspense, useEffect, useMemo } from 'react'
import { DndProvider } from 'react-dnd'
import { HTML5Backend } from 'react-dnd-html5-backend'
import { Routes, Route, useLocation, useNavigate } from 'react-router-dom'

import { useQuery } from '@apollo/client/react'

import {
  HostNavigationProvider,
  ReduxWrappedNavigator,
} from '@buffer-mono/app-shell'
import type { HostNavigation } from '@buffer-mono/app-shell'
import { QUERY_ACCOUNT } from '@buffer-mono/app-shell/src/common/graphql/account'
import { injectMetaViewportTag } from '~publish/helpers/injectMetaViewportTag'
import { Toaster } from '@buffer-mono/popcorn'

import { Banners } from '~publish/components/Banners'
import { ChangelogNotification } from '~publish/components/Changelog'
import { UpdateAvailableBanner } from '~publish/components/UpdateAvailableBanner'
import { CommandBar } from '~publish/components/CommandBar'
import { Enforce2FARedirect } from '~publish/components/Enforce2FARedirect'
import getErrorBoundary from '~publish/components/ErrorBoundary'
import { lazyPage } from '~publish/helpers/lazyPage'
import { useComposerDialogStore } from '~publish/hooks/composerDialogStore'
import { GuestDraftHandoff } from '~publish/components/GuestDraftHandoff'
import { HelpWidget } from '~publish/components/HelpWidget'
import { HeardAboutPrompt } from '~publish/components/HeardAboutPrompt/HeardAboutPrompt'
import { MainSidebar } from '~publish/components/MainSidebar'
import { PublicGraphQLApiModal } from '~publish/components/PublicGraphQLApiModal'
import { RootLayout } from '~publish/components/RootLayout'
import { RouterNavigateBridge } from '~publish/components/RouterNavigateBridge'
import { InsightsAnnouncementModal } from '~publish/pages/Insights'
import { CommunityCountsProvider } from '~publish/contexts/CommunityCountsContext'
import { useFocusOnRouteChange } from '~publish/hooks/useFocusOnRouteChange'
import { AppPages } from '~publish/legacy/app-pages'
import EmbeddedComposePage from '~publish/legacy/compose-page/components/EmbeddedComposePage'
import { AppModals } from '~publish/legacy/modals'
import { CommunityConfigProvider } from '~publish/pages/Community/CommunityConfig'
import { OnboardingPage } from '~publish/pages/Onboarding'
import SettingsSidebar from '~publish/pages/Settings/SettingsSidebar'
import { Setup2FARequiredPage } from '~publish/pages/Setup2FARequired'

import HelpScoutBeaconUpdater from '../HelpScoutBeaconUpdater'
import LoadingGate from '../LoadingGate'

const ComposerDialog = lazyPage(
  () => import('~publish/components/Composer'),
  'ComposerDialog',
)

// A failed dynamic import (stale deploy, CDN hiccup, ad-blocker) shouldn't
// white-screen the rest of the app.
const ComposerErrorBoundary = getErrorBoundary()

/**
 * Defers `ComposerDialog`'s chunk (and its `@udecode/plate` dependency)
 * until the composer has actually been opened once, rather than loading it
 * unconditionally on every app mount. `hasEverOpened` latches true on first
 * open and never resets, so once loaded the dialog (and its listeners) stay
 * mounted for the rest of the session — closing the composer again doesn't
 * unmount this or re-trigger the import.
 */
const GatedComposerDialog = (): JSX.Element | null => {
  const hasEverOpened = useComposerDialogStore((state) => state.hasEverOpened)
  if (!hasEverOpened) return null
  return (
    <ComposerErrorBoundary>
      <Suspense fallback={null}>
        <ComposerDialog />
      </Suspense>
    </ComposerErrorBoundary>
  )
}

const App = (): JSX.Element => {
  const location = useLocation()
  const navigate = useNavigate()
  const { data } = useQuery(QUERY_ACCOUNT, {
    errorPolicy: 'all',
  })

  const channels = useMemo(() => {
    return data?.account?.channels || []
  }, [data])

  // Enable automatic focus management for accessibility (WCAG 2.4.3)
  useFocusOnRouteChange()

  // index.html ships no meta viewport tag; the responsive layout needs one.
  useEffect(() => {
    injectMetaViewportTag()
  }, [])

  const isChannelAuthorizationRoute = location.pathname.startsWith(
    '/channels/authorization',
  )

  // The app shell renders inside this router tree but has no react-router
  // dependency of its own. Hand it our navigation so its banners and modals
  // that link into Publish navigate client-side.
  const hostNavigation = useMemo<HostNavigation>(
    () => ({
      navigate: (path: string): void => {
        void navigate(path)
      },
      pathname: location.pathname,
    }),
    [navigate, location.pathname],
  )

  return (
    <>
      <RouterNavigateBridge />
      <UpdateAvailableBanner />
      <Enforce2FARedirect />
      <Routes>
        <Route path="/onboarding/*" element={<OnboardingPage />} />
        <Route path="/setup-2fa-required" element={<Setup2FARequiredPage />} />
        <Route
          path="/embedded-compose"
          element={
            <LoadingGate>
              <GatedComposerDialog />
              <EmbeddedComposePage />
            </LoadingGate>
          }
        />
        <Route
          path="*"
          element={
            <CommandBar.Provider>
              <CommunityConfigProvider>
                <CommunityCountsProvider channels={channels}>
                  <RootLayout>
                    {/*
                      The app shell is the only thing that assigns
                      `window.appshell` and mounts PreAuthenticationRedirect, so
                      it has to render on every route. The channel-authorization
                      page re-enters the connect flow through both (its Switch
                      Account screen, and the plan-selector CTA on the paywall
                      error), so gating this out left those buttons silently
                      dead. Hide the banners there instead — that is all this
                      slot ever holds, since `hideNavigation` is always on in
                      Publish. See CHAN-12364.
                    */}
                    <RootLayout.Navbar>
                      <HostNavigationProvider value={hostNavigation}>
                        <ReduxWrappedNavigator
                          hideNavigation={true}
                          hideBanners={isChannelAuthorizationRoute}
                        />
                      </HostNavigationProvider>
                    </RootLayout.Navbar>
                    {!isChannelAuthorizationRoute && (
                      <RootLayout.Sidebar>
                        <StrictMode>
                          <Routes>
                            <Route
                              path="/settings/*"
                              element={<SettingsSidebar />}
                            />
                            <Route path="*" element={<MainSidebar />} />
                          </Routes>
                        </StrictMode>
                      </RootLayout.Sidebar>
                    )}
                    <RootLayout.Main className="app-root">
                      <Banners />
                      <Toaster />
                      <LoadingGate>
                        <GatedComposerDialog />
                        <GuestDraftHandoff />
                        <AppPages />
                        <ChangelogNotification />
                      </LoadingGate>
                    </RootLayout.Main>
                  </RootLayout>

                  <HelpWidget account={data?.account ?? null} />

                  <PublicGraphQLApiModal />
                  <InsightsAnnouncementModal />
                  <HeardAboutPrompt />
                  <AppModals />
                  <HelpScoutBeaconUpdater />
                  <CommandBar />
                </CommunityCountsProvider>
              </CommunityConfigProvider>
            </CommandBar.Provider>
          }
        />
      </Routes>
    </>
  )
}

const AppWithDndProvider = (): JSX.Element => (
  <DndProvider backend={HTML5Backend}>
    <App />
  </DndProvider>
)

export default AppWithDndProvider
